import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaAnalyticsGateway1Service } from '../service';
import { GetScheduleAggregatesIO } from '../types';

export const getGetScheduleAggregatesQueryKey = (req: GetScheduleAggregatesIO['input']) =>
  ['AnalyticsGatewayV1', 'GetScheduleAggregates', req] as const;
type QueryKey = ReturnType<typeof getGetScheduleAggregatesQueryKey>;

/**
 * A hook that returns a query for the `GetScheduleAggregates` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetScheduleAggregatesQuery = <E = unknown, D = GetScheduleAggregatesIO['output']>(
  req: GetScheduleAggregatesIO['input'],
  options?: LimitedSchemaQueryOptions<GetScheduleAggregatesIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetScheduleAggregatesIO['output'], E, D, QueryKey>({
    queryKey: getGetScheduleAggregatesQueryKey(req),
    queryFn: () => SchemaAnalyticsGateway1Service.GetScheduleAggregates(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetScheduleAggregates` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetScheduleAggregatesQueries = <E = unknown, D = GetScheduleAggregatesIO['output']>(
  argsArr: {
    req: GetScheduleAggregatesIO['input'];
    options?: LimitedSchemaQueryOptions<GetScheduleAggregatesIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetScheduleAggregatesQueryKey(req),
      queryFn: () => SchemaAnalyticsGateway1Service.GetScheduleAggregates(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetScheduleAggregatesQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetScheduleAggregates` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetScheduleAggregates = () => {
  const queryClient = useQueryClient();

  const fetchGetScheduleAggregates = async <E = unknown, D = GetScheduleAggregatesIO['output']>(
    req: GetScheduleAggregatesIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetScheduleAggregatesIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetScheduleAggregatesIO['output'], E, D, QueryKey>(
      getGetScheduleAggregatesQueryKey(req),
      () => SchemaAnalyticsGateway1Service.GetScheduleAggregates(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetScheduleAggregates;
};
